/**
 * #MT-7740432 — Ondas de derrubada / chamada ativa.
 */
import api from '../api.js';
import db from '../../versa/db.js';
import crud from '../../versa/crud.js';
import router from '../../versa/router.js';
import formset from '../../versa/formset.js';
import dialog from '../../versa/dialog.js';
import msgbox, { notify, ask } from '../../versa/msgbox.js';
import view from '../views/picking-waves.js';
import createForm from '../forms/picking-waves-create.js';
import derrubadaReportForm from '../forms/picking-waves-report.js';
import * as types from '../types.js';
import { consumeWavesPendingFilter } from '../utils/navigate-to-waves.js';
import { resolveLocationIdForMove } from '../utils/location-search.js';

function downloadBase64(base64Content, filename, mime)
{
	const bin = atob(base64Content);
	const arr = new Uint8Array(bin.length);
	for (let i = 0; i < bin.length; i++)
		arr[i] = bin.charCodeAt(i);
	const blob = new Blob([arr], { type: mime || 'application/octet-stream' });
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = filename || 'download';
	a.click();
	URL.revokeObjectURL(a.href);
}

function isCountQuery(query)
{
	const qs = new URLSearchParams(String(query || '').replace(/^\?/, ''));
	return qs.get('group') === 'count';
}

function pageBounds(query)
{
	const qs = new URLSearchParams(String(query || '').replace(/^\?/, ''));
	const limit = Number(qs.get('limit') || 50);
	const offset = Number(qs.get('offset') || 0);
	return {
		limit: Number.isFinite(limit) ? limit : 50,
		offset: Number.isFinite(offset) ? offset : 0,
	};
}

function filtersToParams(filterData)
{
	const params = {};
	for (let i = 0; i < 12; i++)
	{
		const field = filterData?.[`field-${i}`];
		const value = filterData?.[`value-${i}`];
		if (!field || value == null || value === '')
			continue;
		if (['company_id', 'status', 'id'].includes(field))
			params[field] = value;
	}
	return params;
}

function esc(value)
{
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

const WAVE_STATUS_PT = {
	DRAFT: 'Rascunho',
	CONFIRMED: 'Confirmada',
	DESCENDING: 'Descendo',
	PICKING: 'Separação',
	RETURNING: 'Devolvendo',
	DONE: 'Concluída',
	EXCEPTION: 'Exceção',
	CANCELLED: 'Cancelada',
};

const TASK_KIND_PT = {
	DESCER: 'Descer',
	DEVOLVER: 'Devolver',
};

const TASK_STATUS_PT = {
	PENDING: 'Pendente',
	IN_PROGRESS: 'Em andamento',
	DONE: 'Concluída',
	EXCEPTION: 'Exceção',
	DIVERGENCE: 'Divergência',
	CANCELLED: 'Cancelada',
};

const ITEM_STATUS_PT = {
	SELECTED: 'Selecionado',
	RESERVED: 'Reservado',
};

const ROLE_PT = {
	SEPARATOR: 'Separador',
	CHECKER: 'Conferente',
};

const ICO_WAVE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M4 18c2.5-3 4.5-3 7 0s4.5 3 7 0"/><path d="M4 12c2.5-3 4.5-3 7 0s4.5 3 7 0"/><path d="M4 6c2.5-3 4.5-3 7 0s4.5 3 7 0"/></svg>';

const WAVE_HELP_HTML = `
<div class="pw-help-body">
<h4>O que é esta tela</h4>
<p>Aqui o gestor acompanha uma <strong>onda de derrubada</strong>: um lote de pedidos cuja mercadoria ainda está no elevado. O empilhador desce o palete no <strong>chão da mesma coluna</strong>; o separador pega no chão; a sobra volta para o elevado.</p>
<h4>Cabeçalho</h4>
<p><strong>Onda:</strong> número da onda nesta empresa.</p>
<p><strong>Criação:</strong> data e hora em que a onda foi gerada (dia/mês/ano e hora).</p>
<p><strong>Armazém:</strong> armazém da onda (número interno do cadastro).</p>
<p><strong>Status da onda:</strong></p>
<ul>
<li><strong>Rascunho</strong> — ainda não confirmada.</li>
<li><strong>Confirmada</strong> — gerada, sem tarefa de descer pendente.</li>
<li><strong>Descendo</strong> — há palete para descer no chão.</li>
<li><strong>Separação</strong> — paletes no chão; o separador já pode trabalhar.</li>
<li><strong>Devolvendo</strong> — sobra para devolver ao elevado.</li>
<li><strong>Concluída</strong> — onda encerrada.</li>
<li><strong>Exceção</strong> — alguma tarefa travou (chão ocupado, endereço divergente, etc.).</li>
</ul>
<h4>Cards do resumo</h4>
<ul>
<li><strong>Pedidos</strong> — quantos pedidos entram nesta onda.</li>
<li><strong>Descer pendentes</strong> — paletes ainda no elevado, esperando o empilhador.</li>
<li><strong>Descer concluídas</strong> — paletes já no chão da coluna.</li>
<li><strong>Exceções</strong> — tarefas com problema (precisam de tratativa).</li>
<li><strong>Devolver pendentes</strong> — sobras no chão que ainda precisam voltar ao elevado.</li>
</ul>
<h4>Pedidos</h4>
<ul>
<li><strong>Pedido</strong> — número do Pedido de Saída no SIAL.</li>
<li><strong>Ref.</strong> — referência do cliente / documento.</li>
<li><strong>Saída</strong> — número da saída gerada (se ainda não houver, aparece traço).</li>
<li><strong>Status</strong> — <em>Selecionado</em> (na onda) ou <em>Reservado</em> (estoque já reservado).</li>
<li><strong>Emerg.</strong> — <em>Sim</em> quando o pedido é emergencial (prioridade).</li>
</ul>
<h4>Tarefas do empilhador</h4>
<ul>
<li><strong>Tipo Descer</strong> — levar o palete da origem (elevado) para o destino (chão da coluna).</li>
<li><strong>Tipo Devolver</strong> — devolver a sobra do chão para o elevado, depois da separação daquele palete.</li>
<li><strong>Origem / Destino</strong> — endereços (corredor-coluna-nível-posição).</li>
<li><strong>Status da tarefa</strong> — Pendente, Em andamento, Concluída, Exceção, Divergência ou Cancelada.</li>
<li><strong>Confirmar</strong> — quando o empilhador já desceu o palete, informe o <strong>endereço de destino</strong> (ex.: 2-O-46-1). Não é necessário saber o ID interno da posição.</li>
</ul>
<h4>Atribuição / forçar operador</h4>
<ul>
<li><strong>Pedido</strong> — número do pedido da onda que vai receber o operador.</li>
<li><strong>Papel</strong> — Separador (picking) ou Conferente.</li>
<li><strong>Operador</strong> — pessoa que assume aquele pedido (forçar exige motivo).</li>
<li><strong>Distribuir entre</strong> — marca várias pessoas; o sistema reparte os pedidos (proximidade depois do prazo de entrega).</li>
<li><strong>Motivo</strong> — obrigatório ao clicar em <em>Forçar operador</em> (fica na auditoria).</li>
</ul>
<h4>Fila, exceções e rastreio</h4>
<ul>
<li><strong>Fila da onda</strong> — pedidos já liberados para o separador (palete no chão). Vazia se ainda estiver no elevado ou sem saída.</li>
<li><strong>Exceções</strong> — tarefas que não puderam concluir (ex.: chão da coluna ocupado).</li>
<li><strong>Rastreio</strong> — movimentos de estoque gravados nesta derrubada (pedido → onda → tarefa → transação).</li>
</ul>
</div>`;

const DASH_HELP_HTML = `
<div class="pw-help-body">
<h4>O que é este painel</h4>
<p>Resumo da derrubada <strong>da empresa escolhida</strong>: quantas ondas existem, o que está aberto, produtividade do empilhador e exceções que precisam de tratativa.</p>
<h4>Empresa</h4>
<p>Troque a empresa no combo do cabeçalho. Os números e as tabelas passam a ser só daquela empresa.</p>
<h4>Cards do resumo</h4>
<ul>
<li><strong>Ondas</strong> — total de ondas geradas nesta empresa.</li>
<li><strong>Pedidos</strong> — pedidos que entram nessas ondas.</li>
<li><strong>Abertas</strong> — ondas ainda em andamento (não concluídas).</li>
<li><strong>Concluídas</strong> — ondas encerradas.</li>
<li><strong>Exceções</strong> — tarefas com problema (chão ocupado, coluna sem chão, endereço divergente).</li>
</ul>
<h4>Últimas ondas</h4>
<p>As ondas mais recentes, com status, quantidade de pedidos, Descer pendente e exceções. Status em português (Descendo, Exceção, Concluída, etc.).</p>
<h4>Produtividade do empilhador</h4>
<p>Contagem de tarefas <em>confirmadas</em>: quantos Descer, quantos Devolver e quantas exceções por operador.</p>
<h4>Atribuições</h4>
<p>Quem está como Separador ou Conferente em pedidos da onda. Sem linha = ainda ninguém foi distribuído nem forçado.</p>
<h4>Exceções abertas</h4>
<p>Lista o que está travado agora (onda, tarefa, tipo Descer/Devolver e o motivo). Trate na onda correspondente.</p>
</div>`;

function openPwHelp(title, html)
{
	let mb;
	mb = msgbox({
		title,
		text: html,
		buttons: [{ label: 'Fechar', cls: 'bn_Enter' }],
		callback: () => mb.destroy(),
	});
}

const PW_DETAIL_CSS = `
	.sial-detail .sdp-icon-col {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.35rem;
		flex-shrink: 0;
	}
	.sial-detail .pw-help {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 1.35rem;
		height: 1.35rem;
		padding: 0;
		border: 1px solid #9ca3af;
		border-radius: 999px;
		background: #f3f4f6;
		color: #374151;
		font-size: 0.85rem;
		font-weight: 700;
		line-height: 1;
		cursor: pointer;
	}
	.sial-detail .pw-help:hover { background: #e5e7eb; }
	.sial-detail .pw-help:focus { outline: 2px solid #7c3aed; outline-offset: 2px; }
	.pw-help-body { max-height: 65vh; overflow-y: auto; text-align: left; }
	.pw-help-body h4 {
		margin: 0.85rem 0 0.35rem 0;
		font-size: 0.78rem;
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.03em;
		color: #1f2937;
	}
	.pw-help-body h4:first-child { margin-top: 0; }
	.pw-help-body p, .pw-help-body li {
		margin: 0 0 0.35rem 0;
		font-size: 0.875rem;
		line-height: 1.45;
		color: #374151;
	}
	.pw-help-body ul { margin: 0.25rem 0 0.5rem 1.15rem; padding: 0; }
`;

function labelOf(map, value)
{
	if (value == null || value === '')
		return '—';
	const key = String(value);
	return map[key] || map[key.toUpperCase()] || key;
}

function waveBadgeClass(status)
{
	const s = String(status || '').toUpperCase();
	if (s === 'DONE')
		return 'sdp-badge sdp-badge--free';
	if (s === 'EXCEPTION')
		return 'sdp-badge sdp-badge--blocked';
	if (s === 'DESCENDING' || s === 'RETURNING' || s === 'PICKING')
		return 'sdp-badge sdp-badge--reserved';
	return 'sdp-badge sdp-badge--other';
}

function formatSummary(rows)
{
	if (!rows?.length)
		return 'Nenhuma linha na simulação.';
	return rows.map((r) =>
		`Pedido ${r.order_id}${r.reference ? ' (' + r.reference + ')' : ''}: ${r.fulfillment ?? '—'}% — ${r.status || ''}`
	).join('\n');
}

function resolveDatalistId(inp)
{
	const raw = String(inp?.value || '').trim();
	if (!raw)
		return '';
	if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw))
		return raw;
	const lid = inp?.getAttribute('list');
	const dl = lid ? document.getElementById(lid) : null;
	if (dl)
	{
		for (const o of dl.options)
		{
			if (String(o.value) === raw || (o.textContent || '').trim() === raw)
				return o.value;
		}
	}
	return raw;
}

function resolveCompanyIdFromForm(form)
{
	return resolveDatalistId(form.querySelector('#company_id'));
}

function fmtDate(value)
{
	if (!value)
		return '';
	const s = String(value);
	if (/^\d{4}-\d{2}-\d{2}/.test(s))
		return s.slice(0, 10).split('-').reverse().join('/');
	return s;
}

/** Criação / data-hora na tela: DD/MM/AAAA - HH:MM:SS */
function formatDateTimeBr(value)
{
	if (value == null || value === '')
		return '—';
	let s = String(value).trim();
	if (/^\d{4}-\d{2}-\d{2} /.test(s))
		s = s.replace(' ', 'T');
	const d = new Date(s);
	if (Number.isNaN(d.getTime()))
		return String(value);
	const pad = (n) => String(n).padStart(2, '0');
	return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} - ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

async function fillCompanySelect(form)
{
	const sel = form.querySelector('#company_id');
	if (!sel || sel.tagName !== 'SELECT')
		return;
	let companies = [];
	try
	{
		companies = await db.list('companies') || [];
	}
	catch (_)
	{
		companies = [];
	}
	if (!companies.length)
	{
		try
		{
			companies = await api.call(api.list_companies, '?limit=0') || [];
			if (companies.length)
				await db.set('companies', companies).catch(() => {});
		}
		catch (_)
		{
			companies = [];
		}
	}
	sel.innerHTML = companies.length
		? `<option value="">— Selecione a empresa —</option>${
			companies.map((c) =>
				`<option value="${esc(c.id)}">${esc(c.nick || c.name || c.id)}</option>`
			).join('')
		}`
		: '<option value="">— Nenhuma empresa cadastrada —</option>';
	if (companies.length === 1)
	{
		sel.value = String(companies[0].id);
		sel.dispatchEvent(new Event('change', { bubbles: true }));
	}
	else if (!companies.length)
		notify('Nenhuma empresa disponível. Recarregue a tela (F5) após a API responder.', 'Derrubada');
}

function resolveWarehouseId(form)
{
	const sel = form.querySelector('#warehouse_id');
	const n = Number(sel?.value);
	return Number.isInteger(n) && n > 0 ? n : '';
}

function warehouseIsRequired(form)
{
	const sel = form.querySelector('#warehouse_id');
	return Boolean(sel && !sel.disabled && sel.required);
}

function parseOrderIdInput(raw)
{
	const s = String(raw ?? '').trim();
	if (!s)
		return undefined;
	const digits = s.replace(/\D/g, '');
	if (!digits)
		return undefined;
	const n = Number(digits);
	return Number.isFinite(n) && n > 0 ? n : undefined;
}

function collectCreateFilters(form)
{
	const company_id = resolveCompanyIdFromForm(form);
	const warehouse_id = resolveWarehouseId(form);
	const order_id = parseOrderIdInput(form.querySelector('#order_id')?.value);
	const reference = form.querySelector('#reference')?.value;
	const customerRaw = resolveDatalistId(form.querySelector('#customer_id'));
	const routeRaw = resolveDatalistId(form.querySelector('#route_id'));
	const programRaw = resolveDatalistId(form.querySelector('#program_id'));
	const drsRaw = (form.querySelector('#drs_id')?.value || '').trim();
	const date_from = form.querySelector('#date_from')?.value;
	const date_to = form.querySelector('#date_to')?.value;
	const sla_to = form.querySelector('#sla_to')?.value;
	const include_expired = form.querySelector('#include_expired')?.checked;
	const is_emergency = form.querySelector('#is_emergency')?.checked;
	const confirm = form.querySelector('#confirm')?.checked;
	return {
		company_id, warehouse_id, order_id, reference,
		customer_id: customerRaw, route_id: routeRaw, program_id: programRaw,
		drs_id: drsRaw, date_from, date_to, sla_to, include_expired, is_emergency, confirm,
	};
}

function filtersPayload(d)
{
	const customerId = d.customer_id && /^\d+$/.test(String(d.customer_id)) ? Number(d.customer_id) : undefined;
	const routeId = d.route_id && /^\d+$/.test(String(d.route_id)) ? Number(d.route_id) : undefined;
	const programId = d.program_id && /^\d+$/.test(String(d.program_id)) ? Number(d.program_id) : undefined;
	const drsId = d.drs_id && /^\d+$/.test(String(d.drs_id)) ? Number(d.drs_id) : undefined;
	return {
		order_id: d.order_id || undefined,
		reference: d.reference || undefined,
		customer_id: customerId,
		route_id: routeId,
		program_id: programId,
		drs_id: drsId,
		date_from: d.date_from || undefined,
		date_to: d.date_to || undefined,
		sla_to: d.sla_to || undefined,
		is_emergency: d.is_emergency || undefined,
	};
}

function renderOrderTable(box, orders, pick)
{
	if (!box)
		return;
	const list = orders || [];
	if (!list.length)
	{
		box.innerHTML = '<p class="hint">Nenhum pedido elegível com estes filtros. Só entram pedidos <strong>aprovados</strong>, sem saída ativa e que não são devolução.</p>';
		return;
	}
	box.innerHTML = `
		<table class="view">
			<thead><tr>
				<th><input type="checkbox" id="pw-ord-all" title="Marcar / desmarcar os da pesquisa"></th>
				<th>Pedido</th><th>Referência</th><th>Cliente</th><th>Rota</th><th>Programa</th>
				<th>Emerg.</th><th>Prev. entrega</th><th>Criação</th>
			</tr></thead>
			<tbody>
				${list.map((o) => {
					const on = pick.ids.has(Number(o.id));
					return `
					<tr class="${on ? 'pw-on' : ''}" data-oid="${esc(o.id)}">
						<td><input type="checkbox" class="pw-ord" value="${esc(o.id)}" ${on ? 'checked' : ''}></td>
						<td>${esc(o.id)}</td>
						<td>${esc(o.reference)}</td>
						<td>${esc(o.customer_name || o.customer_nick)}</td>
						<td>${esc(o.route_code || '—')}</td>
						<td>${esc(o.program_name)}</td>
						<td>${o.is_emergency ? 'Sim' : ''}</td>
						<td>${esc(fmtDate(o.expected_delivery_date))}</td>
						<td>${esc(fmtDate(o.created_on))}</td>
					</tr>`;
				}).join('')}
			</tbody>
		</table>
	`;
}

function selectedOrderIds(pick)
{
	return [...pick.ids].filter(Boolean);
}

/**
 * Confirmação de geração: botão positivo primeiro (foco + Enter).
 * O ask() padrão foca em «Não» — Enter cancelava em silêncio e o POST create nunca saía.
 */
function confirmGenerateWave(orderCount, onYes)
{
	let mb;
	mb = msgbox({
		title: 'Confirmar derrubada',
		text: `Gerar onda com ${orderCount} pedido(s)? Isso reserva estoque e cria a derrubada de verdade (Simular só mostra o atendimento).`,
		buttons: [
			{ label: 'Sim, gerar onda', cls: 'bn_Enter' },
			{ label: 'Cancelar', cls: 'bn_Escape' },
		],
		callback: (key) =>
		{
			mb.destroy();
			if (key === 'Enter' || key === 'Y')
				onYes();
		},
	});
}

function syncPickCount(form, pick)
{
	const el = form.querySelector('#pw-selected');
	if (el)
		el.textContent = String(pick.ids.size);
	form.querySelectorAll('#pw-orders tr[data-oid]').forEach((tr) =>
	{
		const id = Number(tr.getAttribute('data-oid'));
		const on = pick.ids.has(id);
		tr.classList.toggle('pw-on', on);
		const cb = tr.querySelector('.pw-ord');
		if (cb)
			cb.checked = on;
	});
}

const pickingWaves = function ()
{
	const waves_crud = crud.create({

		insertRecord()
		{
			this.openCreate();
		},

		/**
		 * Abertura com filtro vindo de outra tela (ex.: Dose Certa → link da onda).
		 */
		async run(module_name, label, target_node, grid_view, record_form, fieldspecs, operations, reports, options = {})
		{
			const pending = consumeWavesPendingFilter();
			await Object.getPrototypeOf(this).run.call(
				this, module_name, label, target_node, grid_view, record_form, fieldspecs, operations, reports,
				{ ...options, skipInitialLoad: Boolean(pending) || Boolean(options.skipInitialLoad) },
			);
			if (!pending)
				return;
			this.filter = pending;
			this.filterExplicitlyApplied = true;
			try { this.persistKeepFilterSnapshot?.(); } catch { /* ignore */ }
			await this.loadPage(1);
		},

		async loadBaseData()
		{
			const q = this.queryAll();
			const [companies, warehouses, users, customers, programs, routes] = await Promise.all([
				api.call(api.list_companies, q),
				api.call(api.list_warehouses, q),
				api.call(api.list_users, q).catch(() => []),
				api.call(api.list_customers, q).catch(() => []),
				api.call(api.list_programs, q).catch(() => []),
				api.call(api.list_customer_routes, q).catch(() => []),
			]);
			await Promise.all([
				db.set('companies', companies || []).catch(() => {}),
				db.set('warehouses', warehouses || []).catch(() => {}),
				db.set('users', Array.isArray(users) ? users : (users?.rows || [])).catch(() => {}),
				db.set('customers', customers || []).catch(() => {}),
				db.set('programs', programs || []).catch(() => {}),
				db.set('customer_routes', routes || []).catch(() => {}),
			]);
			this._usersCache = Array.isArray(users) ? users : (users?.rows || []);
		},

		async queryData(query)
		{
			const filterParams = filtersToParams(this.filter || {});
			let companies = filterParams.company_id
				? [{ id: filterParams.company_id }]
				: await db.list('companies').catch(() => []);
			if (!companies?.length)
				companies = await api.call(api.list_companies, '?limit=0') || [];
			if (!companies.length)
				return isCountQuery(query) ? 0 : [];

			const nickById = {};
			for (const c of companies)
				nickById[c.id] = c.nick || c.name || c.id;

			const lists = await Promise.all(companies.map(async (company) =>
			{
				try
				{
					const data = await api.call(api.list_picking_waves, company.id, '?limit=200');
					const rows = data?.rows || [];
					return rows.map((r) => ({
						...r,
						company_id: company.id,
						company_nick: nickById[company.id],
					}));
				}
				catch (e)
				{
					console.warn('picking-waves list', company.id, e);
					return [];
				}
			}));
			let rows = lists.flat();
			if (filterParams.status)
				rows = rows.filter((r) => String(r.status) === String(filterParams.status));
			if (filterParams.id)
				rows = rows.filter((r) => Number(r.id) === Number(filterParams.id));
			rows.sort((a, b) => Number(b.id) - Number(a.id));
			if (isCountQuery(query))
				return rows.length;
			const { limit, offset } = pageBounds(query);
			return rows.slice(offset, offset + limit);
		},

		async loadRecord(id)
		{
			const [company_id, wave_id] = JSON.parse(id);
			const data = await api.call(api.select_picking_wave, company_id, wave_id);
			if (!data?.wave)
				return null;
			this.showDetail(data, company_id);
			return null;
		},

		showDetail(data, company_id)
		{
			const w = data?.wave || {};
			const k = data?.kpis || {};
			const items = data?.items || [];
			const tasks = data?.tasks || [];
			const assignments = data?.assignments || [];
			const exceptions = data?.exceptions || [];
			const queue = data?.picking_queue || [];
			const trace = data?.trace || {};
			const users = (this._usersCache || []);
			const userOpts = users.map((u) =>
				`<option value="${esc(u.id)}">${esc(u.username || u.id)}</option>`
			).join('');
			const waveId = w.id ?? '—';
			const statusPt = labelOf(WAVE_STATUS_PT, w.status);
			const html = `
				<style>${PW_DETAIL_CSS}</style>
				<form id="picking-wave-detail" class="form sial-modal-form sial-detail" autocomplete="off">
					<header class="sdp-head">
						<div class="sdp-head-main">
							<div class="sdp-icon-col">
								<div class="sdp-icon">${ICO_WAVE}</div>
								<button type="button" class="pw-help" id="pw-wave-help" title="Ajuda: o que cada dado significa" aria-label="Ajuda sobre a onda de derrubada">?</button>
							</div>
							<div class="sdp-head-copy">
								<p class="sdp-kicker">Derrubada — onda</p>
								<h2 class="sdp-title">Onda ${esc(waveId)}</h2>
								<div class="sdp-meta">
									<span>Criação: <b>${esc(formatDateTimeBr(w.created_on))}</b></span>
									<span>Armazém: <b>${esc(w.warehouse_id ?? '—')}</b></span>
									<span>Status: <span class="${waveBadgeClass(w.status)}">${esc(statusPt)}</span></span>
								</div>
							</div>
						</div>
						<button type="button" class="bn_Escape sdp-close" title="Fechar" aria-label="Fechar">×</button>
					</header>
					<section class="sdp-kpis" aria-label="Resumo da onda">
						<div class="sdp-kpi sdp-kpi--total">
							<div class="sdp-kpi-label">Pedidos</div>
							<div class="sdp-kpi-value"><span>${esc(k.orders ?? items.length)}</span></div>
						</div>
						<div class="sdp-kpi sdp-kpi--reserved">
							<div class="sdp-kpi-label">Descer pendentes</div>
							<div class="sdp-kpi-value"><span>${esc(k.descer_pending ?? 0)}</span></div>
						</div>
						<div class="sdp-kpi sdp-kpi--avail">
							<div class="sdp-kpi-label">Descer ok (${esc(k.descer_pct ?? 0)}%)</div>
							<div class="sdp-kpi-value"><span>${esc(k.descer_done ?? 0)}</span><small>/${esc(k.descer_total ?? 0)}</small></div>
						</div>
						<div class="sdp-kpi sdp-kpi--blocked">
							<div class="sdp-kpi-label">Exceções</div>
							<div class="sdp-kpi-value"><span>${esc(k.descer_exception ?? 0)}</span></div>
						</div>
						<div class="sdp-kpi">
							<div class="sdp-kpi-label">Em separação</div>
							<div class="sdp-kpi-value"><span>${esc(k.saidas_em_separacao ?? 0)}</span></div>
						</div>
						<div class="sdp-kpi">
							<div class="sdp-kpi-label">Conferidas</div>
							<div class="sdp-kpi-value"><span>${esc(k.saidas_conferidas ?? 0)}</span></div>
						</div>
						<div class="sdp-kpi sdp-kpi--free">
							<div class="sdp-kpi-label">Faturadas+</div>
							<div class="sdp-kpi-value"><span>${esc(k.saidas_faturadas ?? 0)}</span></div>
						</div>
						<div class="sdp-kpi">
							<div class="sdp-kpi-label">Concluídas</div>
							<div class="sdp-kpi-value"><span>${esc(k.saidas_concluidas ?? 0)}</span></div>
						</div>
					</section>
					<section class="sdp-stack">
						<div class="sdp-block">
							<h3 class="sdp-block-title">Andamento da onda</h3>
							<p class="sdp-hint">Acompanhe a derrubada (Descer) e a evolução das saídas geradas por esta onda até a fatura/conclusão.</p>
							<div class="pw-progress" style="display:flex;flex-wrap:wrap;gap:0.75rem;margin:0.5rem 0 0.75rem">
								<div style="flex:1;min-width:10rem;background:#f1f5f9;border-radius:0.4rem;padding:0.5rem 0.65rem">
									<div style="font-size:0.8rem;color:#475569">Descer</div>
									<div style="font-weight:700">${esc(k.descer_done ?? 0)} / ${esc(k.descer_total ?? 0)} (${esc(k.descer_pct ?? 0)}%)</div>
									<div style="height:0.4rem;background:#e2e8f0;border-radius:999px;margin-top:0.35rem;overflow:hidden">
										<div style="height:100%;width:${esc(Math.min(100, Number(k.descer_pct) || 0))}%;background:#16a34a"></div>
									</div>
								</div>
								<div style="flex:1;min-width:10rem;background:#f1f5f9;border-radius:0.4rem;padding:0.5rem 0.65rem;font-size:0.85rem">
									<div><b>Sem saída:</b> ${esc(k.saidas_sem_documento ?? 0)} · <b>Criadas:</b> ${esc(k.saidas_criadas ?? 0)}</div>
									<div><b>Separação/conf.:</b> ${esc((Number(k.saidas_em_separacao) || 0) + (Number(k.saidas_conferidas) || 0))}</div>
									<div><b>Faturadas+:</b> ${esc(k.saidas_faturadas ?? 0)} · <b>Concluídas:</b> ${esc(k.saidas_concluidas ?? 0)}</div>
								</div>
							</div>
						</div>
						<div class="sdp-block">
							<h3 class="sdp-block-title">Pedidos</h3>
							${items.some((i) => i.output_id == null)
								? '<p class="sdp-hint">Pedido sem número em <strong>Saída</strong> ainda não gerou documento em <strong>Movimentação → Saídas</strong>. A onda de verdade gera a saída na hora (reserva + descer).</p>'
								: ''}
							<table class="view" style="width:100%">
								<thead><tr><th>Pedido</th><th>Ref.</th><th>Saída</th><th>Status saída</th><th>Item onda</th><th>Emerg.</th></tr></thead>
								<tbody>
									${items.map((i) => `<tr>
										<td>${esc(i.output_order_id)}</td>
										<td>${esc(i.reference)}</td>
										<td>${esc(i.output_id ?? '—')}</td>
										<td>${esc(i.output_status_label || (i.output_id == null ? '—' : i.output_status))}</td>
										<td>${esc(labelOf(ITEM_STATUS_PT, i.status))}</td>
										<td>${i.is_emergency ? 'Sim' : ''}</td>
									</tr>`).join('') || '<tr><td colspan="6">Sem itens</td></tr>'}
								</tbody>
							</table>
						</div>
						<div class="sdp-block">
							<h3 class="sdp-block-title">Tarefas do empilhador</h3>
							<p class="sdp-hint">Só <strong>Descer</strong> — informe a <strong>posição de destino</strong> (endereço, ex.: <code>2-O-46-1</code>). Devolver está desabilitado.</p>
							<table class="view" style="width:100%">
								<thead><tr><th>Nº</th><th>Tipo</th><th>Origem</th><th>Destino</th><th>Status</th><th></th></tr></thead>
								<tbody>
									${tasks.map((t) => `<tr>
										<td>${esc(t.id)}</td><td>${esc(labelOf(TASK_KIND_PT, t.kind))}</td>
										<td>${esc(t.source_address || t.source_location_id)}</td>
										<td>${esc(t.dest_address || t.dest_location_id || '— (operador define)')}</td>
										<td>${esc(labelOf(TASK_STATUS_PT, t.status))}${t.exception_reason ? ' — ' + esc(t.exception_reason) : ''}</td>
										<td>${t.kind === 'DESCER' && ['PENDING', 'IN_PROGRESS', 'DIVERGENCE'].includes(t.status)
											? `<button type="button" class="button pw-confirm-task" data-task='${esc(JSON.stringify({
												id: t.id, kind: t.kind,
												source_location_id: t.source_location_id,
												source_address: t.source_address || null,
												dest_location_id: t.dest_location_id,
												dest_address: t.dest_address || null,
												warehouse_id: t.warehouse_id || w.warehouse_id,
												lot_id: t.lot_id,
											}))}'>Confirmar</button>`
											: ''}
										</td>
									</tr>`).join('') || '<tr><td colspan="6">Sem tarefas</td></tr>'}
								</tbody>
							</table>
						</div>
						<div class="sdp-block">
							<h3 class="sdp-block-title">Atribuição / forçar operador</h3>
							<div class="sdp-fields">
								<label>Pedido <input id="pw-asg-order" type="number"></label>
								<label>Papel
									<select id="pw-asg-role">
										<option value="SEPARATOR">Separador</option>
										<option value="CHECKER">Conferente</option>
									</select>
								</label>
								<label>Operador
									<select id="pw-asg-user">${userOpts}</select>
								</label>
								<label>Distribuir entre
									<select id="pw-asg-users" multiple size="4">${userOpts}</select>
								</label>
								<label class="sdp-span-2">Motivo (obrigatório ao forçar)
									<input id="pw-asg-reason" type="text">
								</label>
							</div>
							<div class="sdp-check-row" style="margin-top:0.75rem">
								<button type="button" class="button" id="pw-asg-force">Forçar operador</button>
								<button type="button" class="button" id="pw-asg-unassign">Retirar atribuição</button>
								<button type="button" class="button" id="pw-asg-prio">Priorizar pedido</button>
								<button type="button" class="button" id="pw-asg-auto">Distribuir (proximidade após SLA)</button>
							</div>
							<p class="sdp-hint">Atribuições: ${assignments.map((a) => `${esc(a.output_order_id)} ${esc(labelOf(ROLE_PT, a.role))} → ${esc(a.username)}${a.forced ? ' (forçado)' : ''}`).join('; ') || '—'}</p>
						</div>
						<div class="sdp-block">
							<h3 class="sdp-block-title">Fila da onda (separador)</h3>
							<table class="view" style="width:100%">
								<thead><tr><th>Pedido</th><th>Saída</th><th>Operador</th><th>Linhas</th></tr></thead>
								<tbody>
									${queue.map((q) => `<tr>
										<td>${esc(q.output_order_id)}${q.is_emergency ? ' *' : ''}</td>
										<td>${esc(q.output_id)}</td>
										<td>${esc(q.assigned_username || q.assigned_user_id || '—')}</td>
										<td>${esc((q.lines || []).length)}</td>
									</tr>`).join('') || '<tr><td colspan="4">Fila vazia (terciária ainda no elevado ou sem saída)</td></tr>'}
								</tbody>
							</table>
						</div>
						<div class="sdp-block">
							<h3 class="sdp-block-title">Exceções</h3>
							<ul>${(exceptions.length ? exceptions : []).map((e) =>
								`<li>Tarefa ${esc(e.id)} ${esc(labelOf(TASK_KIND_PT, e.kind))} — ${esc(labelOf(TASK_STATUS_PT, e.status))}: ${esc(e.exception_reason || '')}</li>`
							).join('') || '<li>Nenhuma</li>'}</ul>
						</div>
						<div class="sdp-block">
							<h3 class="sdp-block-title">Rastreio</h3>
							<p class="sdp-hint">Pedido → onda → tarefa → transação</p>
							<ul>${(trace.transactions || []).slice(0, 20).map((tx) =>
								`<li>Transação ${esc(tx.id)} · ${esc(tx.reason)} · qtd ${esc(tx.quantity)}</li>`
							).join('') || '<li>Sem transações da derrubada ainda</li>'}</ul>
						</div>
					</section>
					<div class="sdp-foot">
						<button type="button" class="bn_Escape button">Fechar</button>
					</div>
				</form>
			`;
			const dlg = dialog.create(html, (key) =>
			{
				if (key === 'Y' || key === 'Escape')
					dlg.destroy();
			});
			const root = dlg.node;
			const cid = company_id || w.company_id;
			const wid = w.id;
			root.querySelector('#pw-wave-help')?.addEventListener('click', (ev) =>
			{
				ev.preventDefault();
				ev.stopPropagation();
				openPwHelp('Ajuda — Onda de derrubada', WAVE_HELP_HTML);
			});
			root.querySelectorAll('.pw-confirm-task').forEach((btn) =>
			{
				btn.addEventListener('click', async () =>
				{
					let payload;
					try { payload = JSON.parse(btn.getAttribute('data-task')); }
					catch { return notify('Tarefa inválida.', 'Derrubada'); }
					const typed = window.prompt(
						`Confirmar Descer da tarefa ${payload.id}.\nInforme a posição de destino (endereço, ex.: 2-O-46-1):`,
						payload.dest_address
							? String(payload.dest_address)
							: (payload.dest_location_id != null ? String(payload.dest_location_id) : ''),
					);
					if (typed == null)
						return;
					const destRaw = String(typed).trim();
					if (!destRaw)
						return notify('Informe a posição de destino (endereço ou ID).', 'Derrubada');
					const wh = payload.warehouse_id || w.warehouse_id;
					let destId = NaN;
					try
					{
						destId = await resolveLocationIdForMove(cid, wh, destRaw);
					}
					catch (_)
					{
						destId = NaN;
					}
					// Fallback: API resolve endereço (evita strip errado tipo 2-O-46-1 → 2461)
					const destForApi = Number.isFinite(destId) && destId > 0 ? destId : destRaw;
					if (typeof destForApi === 'number' && !(destForApi > 0))
						return notify('Posição de destino não encontrada neste armazém.', 'Derrubada');
					const destLabel = typeof destForApi === 'number'
						? `${destRaw} (id ${destForApi})`
						: destRaw;
					payload = {
						...payload,
						dest_location_id: destForApi,
						source_location_id: payload.source_location_id,
					};
					ask(`Confirmar Descer da tarefa ${payload.id} para ${destLabel}?`, 'Empilhador', async (ok) =>
					{
						if (ok !== 'Y')
							return;
						try
						{
							await api.call(api.confirm_picking_wave_task, cid, wid, payload.id, payload);
							dlg.destroy();
							notify('Tarefa confirmada.', 'Derrubada');
							this.loadPage();
						}
						catch (e)
						{
							notify(String(e), 'Derrubada');
						}
					});
				});
			});
			root.querySelector('#pw-asg-force')?.addEventListener('click', async () =>
			{
				const order = Number(root.querySelector('#pw-asg-order')?.value);
				const role = root.querySelector('#pw-asg-role')?.value;
				const user_id = root.querySelector('#pw-asg-user')?.value;
				const reason = (root.querySelector('#pw-asg-reason')?.value || '').trim();
				if (!order || !user_id)
					return notify('Informe pedido e operador.', 'Derrubada');
				try
				{
					await api.call(api.assign_picking_wave, cid, wid, {
						output_order_id: order, role, user_id, reason, forced: true,
					});
					notify('Operador forçado (auditoria gravada).', 'Derrubada');
				}
				catch (e)
				{
					notify(String(e), 'Derrubada');
				}
			});
			root.querySelector('#pw-asg-auto')?.addEventListener('click', async () =>
			{
				const selected = [...root.querySelectorAll('#pw-asg-users option:checked')].map((o) => o.value);
				const user_ids = selected.length ? selected : users.map((u) => u.id).filter(Boolean).slice(0, 8);
				if (!user_ids.length)
					return notify('Não há operadores para distribuir.', 'Derrubada');
				try
				{
					const res = await api.call(api.auto_assign_picking_wave, cid, wid, {
						user_ids, role: root.querySelector('#pw-asg-role')?.value || 'SEPARATOR',
					});
					notify(`Distribuídos ${res.assigned || 0} pedido(s).`, 'Derrubada');
				}
				catch (e)
				{
					notify(String(e), 'Derrubada');
				}
			});
			root.querySelector('#pw-asg-unassign')?.addEventListener('click', async () =>
			{
				const order = Number(root.querySelector('#pw-asg-order')?.value);
				const role = root.querySelector('#pw-asg-role')?.value || 'SEPARATOR';
				const reason = (root.querySelector('#pw-asg-reason')?.value || '').trim() || 'Retirada de atribuição';
				if (!order)
					return notify('Informe o pedido.', 'Derrubada');
				try
				{
					const res = await api.call(api.unassign_picking_wave, cid, wid, {
						output_order_id: order, role, reason,
					});
					notify(res.cleared ? 'Atribuição retirada.' : (res.message || 'Sem atribuição.'), 'Derrubada');
				}
				catch (e)
				{
					notify(String(e), 'Derrubada');
				}
			});
			root.querySelector('#pw-asg-prio')?.addEventListener('click', async () =>
			{
				const order = Number(root.querySelector('#pw-asg-order')?.value);
				const reason = (root.querySelector('#pw-asg-reason')?.value || '').trim();
				if (!order)
					return notify('Informe o pedido.', 'Derrubada');
				try
				{
					await api.call(api.prioritize_picking_wave, cid, wid, {
						output_order_id: order, boost: true, reason,
					});
					notify('Pedido marcado como prioridade (emergencial) na fila.', 'Derrubada');
				}
				catch (e)
				{
					notify(String(e), 'Derrubada');
				}
			});
		},

		openCreate()
		{
			const fs = formset.create(createForm, async (ev) =>
			{
				if (ev === 'Escape')
				{
					fs.destroy();
					return;
				}
			});
			fs.init();
			const form = document.querySelector('#picking-wave-create');
			if (!form)
				return;
			void fillCompanySelect(form);
			const pick = { ids: new Set(), cap: 120, lastOrders: [] };
			const eligible = form.querySelector('#pw-eligible');
			const ordersBox = form.querySelector('#pw-orders');
			const simBox = form.querySelector('#pw-sim');
			if (ordersBox)
				ordersBox.innerHTML = '<p class="hint">Escolha a empresa (e o armazém) e clique em <strong>Pesquisar</strong>. Marque na grade os pedidos da onda.</p>';

			const refreshPickUi = () => syncPickCount(form, pick);

			form.querySelector('#company_id')?.addEventListener('change', () =>
			{
				pick.ids.clear();
				pick.lastOrders = [];
				if (ordersBox)
					ordersBox.innerHTML = '';
				if (eligible)
					eligible.textContent = '—';
				refreshPickUi();
			});

			form.querySelector('#pw-preview')?.addEventListener('click', async () =>
			{
				const d = collectCreateFilters(form);
				if (!d.company_id)
					return notify('Informe a empresa.', 'Derrubada');
				try
				{
					const data = await api.call(api.preview_picking_wave, d.company_id, {
						filters: filtersPayload(d),
						limit: 500,
					});
					pick.cap = data.max_orders_per_wave || 120;
					pick.lastOrders = data.orders || [];
					if (eligible)
						eligible.textContent = `${data.eligible_count} (teto ${pick.cap} / lote; até 500 nesta lista)`;
					renderOrderTable(ordersBox, pick.lastOrders, pick);
					refreshPickUi();
				}
				catch (e)
				{
					notify(String(e), 'Derrubada');
				}
			});

			form.querySelector('#pw-clear-filters')?.addEventListener('click', () =>
			{
				for (const id of ['order_id', 'reference', 'customer_id', 'route_id', 'program_id', 'drs_id', 'date_from', 'date_to', 'sla_to'])
				{
					const el = form.querySelector(`#${id}`);
					if (el)
						el.value = '';
				}
				const em = form.querySelector('#is_emergency');
				if (em)
					em.checked = false;
			});

			form.querySelector('#pw-select-cap')?.addEventListener('click', () =>
			{
				if (!pick.lastOrders.length)
					return notify('Pesquise os pedidos primeiro.', 'Derrubada');
				pick.ids.clear();
				for (const o of pick.lastOrders)
				{
					if (pick.ids.size >= pick.cap)
						break;
					pick.ids.add(Number(o.id));
				}
				refreshPickUi();
			});

			form.querySelector('#pw-clear-sel')?.addEventListener('click', () =>
			{
				pick.ids.clear();
				refreshPickUi();
			});

			ordersBox?.addEventListener('change', (ev) =>
			{
				const all = ev.target.closest('#pw-ord-all');
				if (all)
				{
					for (const o of pick.lastOrders)
					{
						if (all.checked)
							pick.ids.add(Number(o.id));
						else
							pick.ids.delete(Number(o.id));
					}
					refreshPickUi();
					return;
				}
				const cb = ev.target.closest('.pw-ord');
				if (!cb)
					return;
				const id = Number(cb.value);
				if (cb.checked)
					pick.ids.add(id);
				else
					pick.ids.delete(id);
				refreshPickUi();
			});

			const runGenerateWave = async () =>
			{
				const d = collectCreateFilters(form);
				if (!d.company_id)
					return notify('Informe a empresa.', 'Derrubada');
				if (warehouseIsRequired(form) && !d.warehouse_id)
					return notify('Há mais de um armazém físico — escolha um.', 'Derrubada');
				const order_ids = selectedOrderIds(pick);
				if (!order_ids.length)
				{
					return notify(
						'Marque na grade ao menos um pedido (coluna checkbox). '
						+ '«Selecionados: 0» não gera onda. Use «Marcar até o teto» ou clique nas linhas. '
						+ 'Simular ≠ Gerar.',
						'Derrubada',
					);
				}
				if (!d.confirm)
				{
					const box = form.querySelector('#confirm');
					if (box)
						box.checked = true;
				}
				const cap = pick.cap || 120;
				const n = Math.min(order_ids.length, cap);
				confirmGenerateWave(n, async () =>
				{
					try
					{
						const created = await api.call(api.create_picking_wave, d.company_id, {
							warehouse_id: d.warehouse_id || null,
							order_ids,
							include_expired: d.include_expired,
							reserve_flag: true,
							confirm: true,
						});
						// http.js já notifica 4xx/5xx e devolve undefined — não repetir aviso genérico
						if (created === undefined || created === null)
							return;
						if (created.id == null)
							return notify('A API não devolveu o número da onda. Tente de novo ou avise o suporte.', 'Derrubada');
						fs.destroy();
						const extra = (created.remainder_order_ids || []).length
							? ` Restante para outro lote: ${created.remainder_order_ids.length}.`
							: '';
						const skipped = (created.skipped_order_ids || []).length
							? ` Sem saída: ${created.skipped_order_ids.length}.`
							: '';
						const exc = (created.exceptions || []).length
							? ` Exceções de Descer: ${created.exceptions.length}.`
							: '';
						notify(`Onda ${created.id} gerada. Pedidos neste lote: ${(created.accepted_order_ids || []).length}.${extra}${skipped}${exc}`, 'Derrubada');
						this.loadPage(1);
					}
					catch (e)
					{
						notify(String(e), 'Derrubada');
					}
				});
			};

			form.querySelector('#pw-simulate')?.addEventListener('click', async () =>
			{
				const d = collectCreateFilters(form);
				if (!d.company_id)
					return notify('Informe a empresa.', 'Derrubada');
				if (warehouseIsRequired(form) && !d.warehouse_id)
					return notify('Há mais de um armazém físico — escolha um.', 'Derrubada');
				const order_ids = selectedOrderIds(pick);
				if (!order_ids.length)
					return notify('Pesquise e marque os pedidos da onda (checkbox na grade).', 'Derrubada');
				try
				{
					const sim = await api.call(api.simulate_picking_wave, d.company_id, {
						warehouse_id: d.warehouse_id || null,
						order_ids,
						include_expired: d.include_expired,
					});
					if (simBox)
						simBox.textContent = formatSummary(sim.summary);
					notify('Simulação concluída (não reservou nem gerou saída). Agora clique em Gerar onda e confirme com «Sim, gerar onda».', 'Derrubada');
				}
				catch (e)
				{
					notify(String(e), 'Derrubada');
				}
			});
			// type=button (não bn_Enter/submit): o dialog Versa interceptava bn_Enter e o POST create não saía.
			form.querySelector('#pw-generate')?.addEventListener('click', () => { void runGenerateWave(); });
		},

		async showDashboard()
		{
			let companies = [];
			try
			{
				companies = await api.call(api.list_companies, '?limit=0') || [];
			}
			catch (_)
			{
				try
				{
					companies = await db.list('companies') || [];
				}
				catch (__)
				{
					companies = [];
				}
			}
			if (!Array.isArray(companies))
				companies = [];
			if (!companies.length)
				return notify('Nenhuma empresa disponível para o painel. Recarregue (F5) ou confira permissão de Empresas.', 'Derrubada');
			const preferred = companies.find((c) => /machadotec/i.test(c.nick || c.name || '')) || companies[0];
			const opts = companies.map((c) =>
				`<option value="${esc(c.id)}"${c.id === preferred.id ? ' selected' : ''}>${esc(c.nick || c.name)}</option>`
			).join('');
			const html = `
				<style>${PW_DETAIL_CSS}
					#picking-wave-dash #pw-dash-company { min-width: 12rem; }
				</style>
				<form id="picking-wave-dash" class="form sial-modal-form sial-detail" autocomplete="off">
					<header class="sdp-head">
						<div class="sdp-head-main">
							<div class="sdp-icon-col">
								<div class="sdp-icon">${ICO_WAVE}</div>
								<button type="button" class="pw-help" id="pw-dash-help" title="Ajuda: o que cada dado significa" aria-label="Ajuda sobre o painel de derrubada">?</button>
							</div>
							<div class="sdp-head-copy">
								<p class="sdp-kicker">Derrubada — painel</p>
								<h2 class="sdp-title">Painel de ondas</h2>
								<div class="sdp-meta">
									<label>Empresa
										<select id="pw-dash-company">${opts}</select>
									</label>
								</div>
							</div>
						</div>
						<button type="button" class="bn_Escape sdp-close" title="Fechar" aria-label="Fechar">×</button>
					</header>
					<section class="sdp-kpis" id="pw-dash-kpis" aria-label="Resumo da derrubada">
						<div class="sdp-kpi sdp-kpi--total"><div class="sdp-kpi-label">Ondas</div><div class="sdp-kpi-value"><span>—</span></div></div>
						<div class="sdp-kpi sdp-kpi--reserved"><div class="sdp-kpi-label">Pedidos</div><div class="sdp-kpi-value"><span>—</span></div></div>
						<div class="sdp-kpi sdp-kpi--avail"><div class="sdp-kpi-label">Abertas</div><div class="sdp-kpi-value"><span>—</span></div></div>
						<div class="sdp-kpi sdp-kpi--free"><div class="sdp-kpi-label">Concluídas</div><div class="sdp-kpi-value"><span>—</span></div></div>
						<div class="sdp-kpi sdp-kpi--blocked"><div class="sdp-kpi-label">Exceções</div><div class="sdp-kpi-value"><span>—</span></div></div>
					</section>
					<section class="sdp-stack" id="pw-dash-body">Carregando…</section>
					<div class="sdp-foot">
						<button type="button" class="bn_Escape button">Fechar</button>
					</div>
				</form>
			`;
			const dlg = dialog.create(html, (key) =>
			{
				if (key === 'Y' || key === 'Escape')
					dlg.destroy();
			});
			dlg.node.querySelector('#pw-dash-help')?.addEventListener('click', (ev) =>
			{
				ev.preventDefault();
				ev.stopPropagation();
				openPwHelp('Ajuda — Painel de derrubada', DASH_HELP_HTML);
			});
			const fill = async (company_id) =>
			{
				const body = dlg.node.querySelector('#pw-dash-body');
				const kpis = dlg.node.querySelector('#pw-dash-kpis');
				try
				{
					const data = await api.call(api.picking_wave_dashboard, company_id);
					const c = data.consolidated || {};
					const waves = data.recent_waves || [];
					const exceptions = data.exceptions || [];
					const prod = data.productivity || {};
					if (kpis)
					{
						kpis.innerHTML = `
							<div class="sdp-kpi sdp-kpi--total">
								<div class="sdp-kpi-label">Ondas</div>
								<div class="sdp-kpi-value"><span>${esc(c.waves ?? 0)}</span></div>
							</div>
							<div class="sdp-kpi sdp-kpi--reserved">
								<div class="sdp-kpi-label">Pedidos</div>
								<div class="sdp-kpi-value"><span>${esc(c.orders ?? 0)}</span></div>
							</div>
							<div class="sdp-kpi sdp-kpi--avail">
								<div class="sdp-kpi-label">Em derrubada</div>
								<div class="sdp-kpi-value"><span>${esc(c.waves_descending ?? 0)}</span></div>
							</div>
							<div class="sdp-kpi sdp-kpi--free">
								<div class="sdp-kpi-label">Em separação</div>
								<div class="sdp-kpi-value"><span>${esc(c.waves_picking ?? 0)}</span></div>
							</div>
							<div class="sdp-kpi">
								<div class="sdp-kpi-label">DESCER pend.</div>
								<div class="sdp-kpi-value"><span>${esc(c.descer_pending ?? 0)}</span></div>
							</div>
							<div class="sdp-kpi">
								<div class="sdp-kpi-label">DESCER ok</div>
								<div class="sdp-kpi-value"><span>${esc(c.descer_done ?? 0)}</span></div>
							</div>
							<div class="sdp-kpi sdp-kpi--blocked">
								<div class="sdp-kpi-label">Exceções</div>
								<div class="sdp-kpi-value"><span>${esc(c.exceptions ?? 0)}</span></div>
							</div>
						`;
					}
					const descerPct = (w) =>
					{
						const tot = Number(w.descer_total) || 0;
						const ok = Number(w.descer_done) || 0;
						return tot ? Math.round(100 * ok / tot) : 0;
					};
					body.innerHTML = `
						<div class="sdp-block">
							<h3 class="sdp-block-title">Andamento das ondas</h3>
							<p class="sdp-hint">Relatório rápido: Descer e evolução das saídas (separação → conferência → fatura). Abra a onda na lista principal para o detalhe completo.</p>
							<table class="view" style="width:100%">
								<thead><tr>
									<th>Onda</th><th>Status</th><th>Pedidos</th>
									<th>Descer</th><th>%</th>
									<th>Sep.</th><th>Conf.</th><th>Fat.+</th><th>Conc.</th><th>Exc.</th>
								</tr></thead>
								<tbody>
									${waves.map((w) => `<tr>
										<td>${esc(w.id)}</td>
										<td>${esc(labelOf(WAVE_STATUS_PT, w.status))}</td>
										<td>${esc(w.order_count)}</td>
										<td>${esc(w.descer_done ?? 0)}/${esc(w.descer_total ?? 0)}</td>
										<td>${esc(descerPct(w))}%</td>
										<td>${esc(w.saidas_em_separacao ?? 0)}</td>
										<td>${esc(w.saidas_conferidas ?? 0)}</td>
										<td>${esc(w.saidas_faturadas ?? 0)}</td>
										<td>${esc(w.saidas_concluidas ?? 0)}</td>
										<td>${esc(w.exception_count ?? 0)}</td>
									</tr>`).join('') || '<tr><td colspan="10">Nenhuma</td></tr>'}
								</tbody>
							</table>
						</div>
						<div class="sdp-block">
							<h3 class="sdp-block-title">Produtividade do empilhador</h3>
							<p class="sdp-hint">Tarefas de <strong>Descer</strong> confirmadas (devolução desabilitada na derrubada)</p>
							<table class="view" style="width:100%">
								<thead><tr><th>Operador</th><th>Descer</th><th>Exceções</th></tr></thead>
								<tbody>
									${(prod.forklift || []).map((p) => `<tr>
										<td>${esc(p.username || '—')}</td><td>${esc(p.descer_done)}</td>
										<td>${esc(p.exceptions)}</td>
									</tr>`).join('') || '<tr><td colspan="3">Sem confirmações</td></tr>'}
								</tbody>
							</table>
						</div>
						<div class="sdp-block">
							<h3 class="sdp-block-title">Atribuições (separação / conferência)</h3>
							<table class="view" style="width:100%">
								<thead><tr><th>Operador</th><th>Papel</th><th>Pedidos</th></tr></thead>
								<tbody>
									${(prod.assignments || []).map((p) => `<tr>
										<td>${esc(p.username)}</td><td>${esc(labelOf(ROLE_PT, p.role))}</td><td>${esc(p.orders)}</td>
									</tr>`).join('') || '<tr><td colspan="3">Sem atribuições</td></tr>'}
								</tbody>
							</table>
						</div>
						<div class="sdp-block">
							<h3 class="sdp-block-title">Exceções abertas</h3>
							<ul>${exceptions.map((e) =>
								`<li>Onda ${esc(e.wave_id)} tarefa ${esc(e.id)} ${esc(labelOf(TASK_KIND_PT, e.kind))} — ${esc(labelOf(TASK_STATUS_PT, e.status))}: ${esc(e.exception_reason || e.source_address || '')}</li>`
							).join('') || '<li>Nenhuma</li>'}</ul>
						</div>
					`;
				}
				catch (e)
				{
					if (body)
						body.textContent = String(e);
				}
			};
			const sel = dlg.node.querySelector('#pw-dash-company');
			sel?.addEventListener('change', () => fill(sel.value));
			if (sel?.value)
				fill(sel.value);
		},
	});

	waves_crud.showDerrubadaReport = function showDerrubadaReport()
	{
		const fs = formset.create(derrubadaReportForm, async (data) =>
		{
			const root = fs.form?.node || document.querySelector('#picking-waves-report-form');
			const format = (root?.querySelector('#format')?.value || data.format || 'json').toLowerCase();
			const viewMode = (root?.querySelector('#view')?.value || data.view || 'waves').toLowerCase();
			const isTasks = viewMode === 'tasks';
			const unitLabel = isTasks ? 'tarefa(s)' : 'onda(s)';
			const companyId = data.company_id;
			if (!companyId)
			{
				notify('Selecione a empresa.', 'Atenção');
				return false;
			}
			const dateFrom = data.date_from || root?.querySelector('#date_from')?.value;
			const dateTo = data.date_to || root?.querySelector('#date_to')?.value;
			if (!dateFrom || !dateTo)
			{
				notify('Informe o período (De e Até).', 'Atenção');
				return false;
			}
			if (dateTo < dateFrom)
			{
				notify('A data "Até" deve ser igual ou posterior à data "De".', 'Atenção');
				return false;
			}
			if (typeof api.picking_waves_derrubada_report !== 'function')
			{
				notify('Frontend desatualizado: recarregue a página com Ctrl+F5.', 'Atenção');
				return false;
			}
			const waveRaw = root?.querySelector('#wave_id')?.value?.trim();
			const status = (root?.querySelector('#status')?.value || '').trim() || undefined;
			const params = {
				format,
				view: viewMode,
				date_from: dateFrom,
				date_to: dateTo,
				wave_id: waveRaw || undefined,
				status,
			};
			fs.destroy();
			try
			{
				if (format === 'json')
				{
					params.limit = 2000;
					params.offset = 0;
					const resp = await api.picking_waves_derrubada_report(companyId, params);
					if (!resp.ok)
					{
						const err = await resp.json().catch(() => ({}));
						notify(err?.error || err?.message || `Erro HTTP ${resp.status}`, 'Erro');
						return;
					}
					const payload = await resp.json();
					const rows = payload?.rows || [];
					if (!rows.length)
					{
						notify('Nenhuma derrubada neste período e filtros.', 'Resultado');
						return;
					}
					const viewTitle = isTasks ? 'Tarefas Descer' : 'Por onda';
					let headersHtml;
					let tbody;
					if (isTasks)
					{
						headersHtml = '<th>Onda</th><th>St.onda</th><th>Tarefa</th><th>St.tarefa</th><th>Origem</th><th>Destino</th><th>SKU</th><th>Lote</th><th>Confirmada</th>';
						tbody = rows.map((r) => `<tr>
							<td>${esc(r.onda)}</td>
							<td>${esc(r.wave_status_label)}</td>
							<td>${esc(r.tarefa)}</td>
							<td>${esc(r.task_status_label)}</td>
							<td>${esc(r.origem || '—')}</td>
							<td>${esc(r.destino || '—')}</td>
							<td>${esc(r.sku || '—')}</td>
							<td>${esc(r.lot_id ?? '—')}</td>
							<td>${esc(r.confirmada_em || '—')}</td>
						</tr>`).join('');
					}
					else
					{
						headersHtml = '<th>Onda</th><th>Status</th><th>Criação</th><th>Armazém</th><th>Ped.</th><th>Desc.pend</th><th>Desc.ok</th><th>%</th><th>Sep.</th><th>Conf.</th><th>Fat.+</th><th>Conc.</th><th>Exc.</th>';
						tbody = rows.map((r) => `<tr>
							<td>${esc(r.onda)}</td>
							<td>${esc(r.status_label)}</td>
							<td>${esc(r.criacao)}</td>
							<td>${esc(r.armazem || '—')}</td>
							<td>${esc(r.pedidos ?? 0)}</td>
							<td>${esc(r.descer_pending ?? 0)}</td>
							<td>${esc(r.descer_done ?? 0)}</td>
							<td>${esc(r.descer_pct ?? 0)}</td>
							<td>${esc(r.saidas_em_separacao ?? 0)}</td>
							<td>${esc(r.saidas_conferidas ?? 0)}</td>
							<td>${esc(r.saidas_faturadas ?? 0)}</td>
							<td>${esc(r.saidas_concluidas ?? 0)}</td>
							<td>${esc(r.exception_count ?? 0)}</td>
						</tr>`).join('');
					}
					const html = `
						<div class="msgbox" style="max-width:96vw;width:1100px;text-align:left">
							<div class="mb-title">Derrubadas (${esc(viewTitle)}) — ${esc(dateFrom)} a ${esc(dateTo)}</div>
							<div style="max-height:62vh;overflow:auto;border:1px solid #cbd5e1;border-radius:4px">
								<table class="view" style="width:100%;font-size:0.8rem">
									<thead><tr>${headersHtml}</tr></thead>
									<tbody>${tbody}</tbody>
								</table>
							</div>
							<p class="fss mts">${rows.length} ${unitLabel}</p>
							<div class="fsb mtm">
								<button type="button" class="button" id="btn-pwr-csv">CSV</button>
								<button type="button" class="button" id="btn-pwr-pdf">PDF</button>
								<button type="button" class="button bn_N">Fechar</button>
							</div>
						</div>`;
					const dlg = dialog.create(html, (key) =>
					{
						if (key === 'N' || key === 'Escape')
							dlg.destroy();
					}, /N|Escape/);
					const exportFmt = async (fmt) =>
					{
						try
						{
							const r2 = await api.picking_waves_derrubada_report(companyId, { ...params, format: fmt });
							if (!r2.ok)
							{
								const err2 = await r2.json().catch(() => ({}));
								notify(err2?.error || err2?.message || `Erro HTTP ${r2.status}`, 'Erro');
								return;
							}
							const result = await r2.json();
							if (fmt === 'csv' && result?.csv)
							{
								downloadBase64(result.csv, result.filename || 'derrubadas.csv', 'text/csv;charset=utf-8');
								notify('Download CSV iniciado.', 'Sucesso');
							}
							else if (fmt === 'pdf' && result?.pdf)
							{
								downloadBase64(result.pdf, result.filename || 'derrubadas.pdf', 'application/pdf');
								notify('Download PDF iniciado.', 'Sucesso');
							}
							else
								notify('Nenhum dado para exportar.', 'Aviso');
						}
						catch (e)
						{
							notify(e?.message || String(e), 'Erro');
						}
					};
					dlg.node.querySelector('#btn-pwr-csv')?.addEventListener('click', () => exportFmt('csv'));
					dlg.node.querySelector('#btn-pwr-pdf')?.addEventListener('click', () => exportFmt('pdf'));
					return;
				}

				const resp = await api.picking_waves_derrubada_report(companyId, params);
				if (!resp.ok)
				{
					const err = await resp.json().catch(() => ({}));
					notify(err?.error || err?.message || `Erro HTTP ${resp.status}`, 'Erro');
					return;
				}
				const result = await resp.json();
				if (format === 'csv' && result?.csv)
				{
					downloadBase64(result.csv, result.filename || 'derrubadas.csv', 'text/csv;charset=utf-8');
					notify(`CSV gerado (${result.rows ?? ''} ${unitLabel}).`, 'Sucesso');
				}
				else if (format === 'pdf' && result?.pdf)
				{
					downloadBase64(result.pdf, result.filename || 'derrubadas.pdf', 'application/pdf');
					notify(`PDF gerado (${result.rows ?? ''} ${unitLabel}).`, 'Sucesso');
				}
				else
					notify('Nenhum dado para exportar.', 'Aviso');
			}
			catch (e)
			{
				notify(e?.message || String(e), 'Erro');
			}
		});
		fs.init();
	};

	const fieldspecs = [
		{ index: 0, field: 'company_id', label: 'Empresa', types: [types.datalist()], store: 'companies', labels: ['nick'] },
		{ index: 1, field: 'id', label: 'Onda', types: [types.number(0)] },
		{ index: 2, field: 'order_count', label: 'Pedidos', types: [types.number(0)] },
		{ index: 3, field: 'descer_pending', label: 'Descer pend.', types: [types.number(0)] },
		{ index: 4, field: 'descer_done', label: 'Descer ok', types: [types.number(0)] },
		{ index: 5, field: 'descer_total', label: 'Descer %', types: [types.number(0)] },
		{ index: 6, field: 'saidas_faturadas', label: 'Fat.+', types: [types.number(0)] },
		{ index: 7, field: 'exception_count', label: 'Exceções', types: [types.number(0)] },
		{ index: 8, field: 'status', label: 'Status', types: [] },
		{ index: 9, field: 'created_on', label: 'Criação', types: [types.datetime()] },
	];
	const operations = [
		{ hash: '#picking-wave-dashboard', label: 'Painel' },
	];
	const reports = [
		{ hash: '#picking-waves-derrubada-report', label: 'Relatório&nbsp;de&nbsp;derrubadas' },
	];

	router.set('#picking-wave-dashboard', () => waves_crud.showDashboard());
	router.set('#picking-waves-derrubada-report', () => waves_crud.showDerrubadaReport());

	waves_crud.run(
		'picking-waves',
		'Derrubada — ondas',
		document.getElementById('workarea'),
		view,
		createForm,
		fieldspecs,
		operations,
		reports,
		{ default_columns: '0,1,2,3,4,5,6,7,8,9' },
	);

	db.list('users').then((rows) => { waves_crud._usersCache = rows || waves_crud._usersCache || []; }).catch(() => {});
};

export default pickingWaves;
